'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import type { PostCardProps } from '@routeroyal/theme-core';

/**
 * Stone Investment Post Card
 * 
 * Editorial-style post card with:
 * - Square image
 * - Greyscale effect
 * - Sophisticated typography
 */
export function StoneInvestmentPostCard({ 
  post, 
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = false,
  showDate = true,
  showReadingTime = false,
  locale = 'en',
  basePath = '/news',
  className = '',
}: PostCardProps) {
  const formattedDate = post.publishedAt 
    ? new Date(post.publishedAt).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const postUrl = `${basePath}/${post.slug}`;

  if (variant === 'featured') {
    return (
      <Link href={postUrl} className={`group block ${className}`}>
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
        >
          {/* Image */}
          <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a]">
            {post.featuredImage?.url ? (
              <Image
                src={post.featuredImage.url}
                alt={post.title}
                fill
                className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a]" />
            )}
          </div>

          {/* Content */}
          <div className="lg:pl-8">
            {showCategory && post.category && (
              <span 
                className="text-[10px] tracking-[0.25em] uppercase text-[var(--color-text-muted)] mb-4 block"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {post.category.name}
              </span>
            )}

            <h3 
              className="text-2xl lg:text-3xl text-[var(--color-text)] mb-4 group-hover:text-[var(--color-text-muted)] transition-colors duration-300"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 400,
              }}
            >
              {post.title}
            </h3>

            {showExcerpt && post.excerpt && (
              <p 
                className="text-[var(--color-text-muted)] text-sm leading-relaxed mb-6 line-clamp-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {post.excerpt}
              </p>
            )}

            <div 
              className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {showDate && formattedDate && <span>{formattedDate}</span>}
              {showReadingTime && post.readingTime && (
                <>
                  <span className="text-[var(--color-border)]">·</span>
                  <span>{post.readingTime} min read</span>
                </>
              )}
            </div>
          </div>
        </motion.article>
      </Link>
    );
  }

  // Default variant
  return (
    <Link href={postUrl} className={`group block ${className}`}>
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1a1a] mb-5">
          {post.featuredImage?.url ? (
            <Image
              src={post.featuredImage.url}
              alt={post.title}
              fill
              className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a]" />
          )}
        </div>

        {/* Content */}
        <div>
          {showCategory && post.category && (
            <span 
              className="text-[10px] tracking-[0.25em] uppercase text-[var(--color-text-muted)] mb-2 block"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {post.category.name}
            </span>
          )}

          <h3 
            className="text-xl text-[var(--color-text)] mb-3 group-hover:text-[var(--color-text-muted)] transition-colors duration-300"
            style={{ 
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontWeight: 400,
            }}
          >
            {post.title}
          </h3>

          {showExcerpt && post.excerpt && (
            <p 
              className="text-[var(--color-text-muted)] text-sm leading-relaxed mb-4 line-clamp-2"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {post.excerpt}
            </p>
          )}

          <div 
            className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {showAuthor && post.author && <span>{post.author.name}</span>}
            {showDate && formattedDate && (
              <>
                {showAuthor && <span className="text-[var(--color-border)]">·</span>}
                <span>{formattedDate}</span>
              </>
            )}
          </div>
        </div>
      </motion.article>
    </Link>
  );
}
